import React, { useState, useEffect } from 'react'
import { Modal, Button, Checkbox, message } from 'antd'
import { ThunderboltOutlined } from '@ant-design/icons'
import moment from 'moment'
import { ApiRequest } from '../../../../services/api/api'
import SweetAlertService from '../../../../services/lib/utils/sweet-alert-service'
import palletSticker from '../../../static/pdf/pallet-stickers/pallet-sticker'
import productStickersPdfGenerateAll from '../../../static/pdf/product-stickers/product-stickers-pdf-generate_all'
import { IStickerPdf } from '../../../../services/model/IStickerPdf'
import { modalStyles, titleStyle, indicatorStyle, gradientHeaderStyle, headerItemStyle, headerLabelStyle, headerValueStyle, modalContainerStyle, tableHeaderStyle, tableBorder, tableCellStyle } from './modal-styles'

interface InspectionItemDetail {
  rawMaterialId: number
  itemCode: string
  description: string
  revAreaQty: number  // 接收区域数量
  putAwayQty: number  // 上架数量
}

interface PurchaseOrderPutawayModalProps {
  visible: boolean
  onClose: () => void
  poData: any
  onSuccess: () => void
  onOpenAutoSplitModal?: (poData: any, itemData: any) => void
  onOpenMergeToPalletModal?: (selectedItems: any[]) => void
  refreshTrigger?: any  // 添加刷新触发器
}

interface PalletItem {
  palletNumber: string
  itemCode: string
  description: string
  quantity: number
  createdAt: string
  selected: boolean
}

const PurchaseOrderPutawayModal: React.FC<PurchaseOrderPutawayModalProps> = ({
  visible,
  onClose,
  poData,
  onSuccess,
  onOpenAutoSplitModal,
  onOpenMergeToPalletModal,
  refreshTrigger
}) => {
  const [loading, setLoading] = useState(false)
  const [palletList, setPalletList] = useState<PalletItem[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set<number>())
  const [packagingInfoMap, setPackagingInfoMap] = useState<Map<number, number>>(new Map())
  const [itemDetailsMap, setItemDetailsMap] = useState<Map<number, InspectionItemDetail>>(new Map())

  useEffect(() => {
    if (visible && poData) {
      // 初始化空托盘列表和清空选中状态
      setPalletList([])
      setSelectedItems(new Set<number>())
      setSelectAll(false)

      // 并行获取所有数据
      fetchPoPallets()  // 获取已有托盘
      fetchAllPackagingInfo()  // 获取包装信息
      fetchItemDetails()  // 获取 Inspection Item 详情
    }
  }, [visible, poData, refreshTrigger])  // 添加 refreshTrigger 依赖

  // 获取 Inspection Item 详情
  const fetchItemDetails = async () => {
    if (!poData?.poId) return

    try {
      const response = await ApiRequest({
        url: `UnloadingInspection/GetAllUnloadingInspection?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (response?.data?.data && response.data.data.length > 0) {
        const inspectionData = response.data.data[0]

        // 提取 unloadingDetail 中的 revAreaQty 和 putAwayQty
        if (inspectionData.unloadingDetail && Array.isArray(inspectionData.unloadingDetail)) {
          const newItemDetailsMap = new Map<number, InspectionItemDetail>()

          inspectionData.unloadingDetail.forEach((detail: any) => {
            // 通过 rawMaterialId 找到对应的 poDetail
            const poDetailItem = poData.poDetail?.find((poDetail: any) =>
              poDetail.rawMaterial?.rawMaterialId === detail.rawMaterialId
            )

            if (poDetailItem) {
              newItemDetailsMap.set(poDetailItem.poDetailId, {
                rawMaterialId: detail.rawMaterialId,
                itemCode: poDetailItem.rawMaterial?.rawMaterialCode || '',
                description: poDetailItem.rawMaterial?.rawMaterialName || '',
                revAreaQty: detail.revAreaQty || 0,
                putAwayQty: detail.putAwayQty || 0
              })
            }
          })

          setItemDetailsMap(newItemDetailsMap)
        }
      }
    } catch (error) {
      // Silently handle fetch errors
    }
  }

  // 获取 PO 的托盘列表
  const fetchPoPallets = async () => {
    if (!poData?.poId) return

    try {
      const response = await ApiRequest({
        url: `PurchaseOrder/GetPoPallets?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (response?.data?.data?.pallets) {
        const pallets: PalletItem[] = response.data.data.pallets.map((pallet: any) => ({
          palletNumber: pallet.palletNumber,
          itemCode: pallet.itemCode,
          description: pallet.description,
          quantity: pallet.qty,
          createdAt: pallet.createdAt,
          selected: false
        }))

        setPalletList(pallets)
      }
    } catch (error) {
      // Silently handle fetch errors
    }
  }

  // 获取所有物料的包装信息
  const fetchAllPackagingInfo = async () => {
    if (!poData?.poDetail) return

    const newPackagingMap = new Map<number, number>()

    // 并行获取所有物料的包装信息（两步流程）
    const promises = poData.poDetail.map(async (item: any) => {
      if (!item.rawMaterial?.rawMaterialId) return

      try {
        // 第一步：通过 rawMaterialId 获取 baseProductId
        const rawMaterialResponse = await ApiRequest({
          url: `RawMaterial/GetRawMaterialById?id=${item.rawMaterial.rawMaterialId}`,
          method: 'get',
          isShowSpinner: false
        })

        if (rawMaterialResponse?.data?.data) {
          const rawMaterialData = rawMaterialResponse.data.data

          // 从 baseProductRawMaterial 数组中获取第一个 baseProductId
          const baseProductRawMaterial = rawMaterialData.baseProductRawMaterial?.[0]
          const baseProductId = baseProductRawMaterial?.baseProductId

          if (baseProductId) {
            // 第二步：通过 baseProductId 获取基本产品的包装信息
            const baseProductResponse = await ApiRequest({
              url: `BaseProduct/GetBaseProductById?id=${baseProductId}`,
              method: 'get',
              isShowSpinner: false
            })

            if (baseProductResponse?.data?.data) {
              const baseProduct = baseProductResponse.data.data
              // 优先级：packagingType.quantity > productPrice[0].quantiy > quantity
              const packagingQty = baseProduct.packagingType?.quantity ||
                                 baseProduct.productPrice?.[0]?.quantiy ||
                                 baseProduct.quantity || 0
              newPackagingMap.set(item.poDetailId, packagingQty)
            }
          } else {
            // 回退：使用 rawMaterial 的包装信息
            const packagingQty = rawMaterialData.quantity || rawMaterialData.packagingQty || 0
            newPackagingMap.set(item.poDetailId, packagingQty)
          }
        }
      } catch (error) {
        // Silently handle fetch errors
      }
    })

    await Promise.all(promises)
    setPackagingInfoMap(newPackagingMap)
  }

  // 获取物料的包装数量
  const getPackagingQty = (item: any) => {
    return packagingInfoMap.get(item.poDetailId) ||
           item.rawMaterial?.baseProductRawMaterial?.[0]?.packagingQty ||
           item.rawMaterial?.baseProductRawMaterial?.[0]?.quantity ||
           item.rawMaterial?.packagingQty ||
           item.rawMaterial?.quantity || 0
  }

  // 获取物料的 Putaway Qty
  const getPutawayQty = (item: any) => {
    const itemDetail = itemDetailsMap.get(item.poDetailId)
    return itemDetail?.putAwayQty || 0
  }

  // 获取物料的 Pending Qty（Receiving Qty = 接收区域的数量）
  const getPendingQty = (item: any) => {
    const itemDetail = itemDetailsMap.get(item.poDetailId)
    return itemDetail?.revAreaQty || 0
  }

  // 处理物料选择
  const handleItemSelect = (poDetailId: number, checked: boolean) => {
    const newSelected = new Set<number>(selectedItems)
    if (checked) {
      newSelected.add(poDetailId)
    } else {
      newSelected.delete(poDetailId)
    }
    setSelectedItems(newSelected)
  }

  // 处理全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<number>(poData?.poDetail?.map((item: any) => item.poDetailId) || [])
      setSelectedItems(allIds)
    } else {
      setSelectedItems(new Set<number>())
    }
  }

  // 计算托盘列表的行合并结构
  const getPalletRowStructure = () => {
    const structure: any[] = []
    let currentPallet: any = null
    let currentPalletItems: any[] = []

    palletList.forEach((item, index) => {
      if (!currentPallet || currentPallet.palletNumber !== item.palletNumber) {
        // 保存上一个托盘
        if (currentPallet) {
          structure.push({
            ...currentPallet,
            items: currentPalletItems,
            rowspan: currentPalletItems.length
          })
        }

        // 开始新托盘
        currentPallet = {
          palletNumber: item.palletNumber,
          createdAt: item.createdAt,
          selected: item.selected
        }
        currentPalletItems = [item]
      } else {
        // 添加到当前托盘
        currentPalletItems.push(item)
      }

      // 最后一个托盘
      if (index === palletList.length - 1) {
        structure.push({
          ...currentPallet,
          items: currentPalletItems,
          rowspan: currentPalletItems.length
        })
      }
    })

    return structure
  }

  const palletStructure = getPalletRowStructure()

  // 全选处理
  const handleSelectAllChange = (checked: boolean) => {
    setSelectAll(checked)
    const newPalletList = palletList.map(item => ({
      ...item,
      selected: checked
    }))
    setPalletList(newPalletList)
  }

  // 单个复选框处理
  const handleItemSelectChange = (index: number, checked: boolean) => {
    const newPalletList = [...palletList]
    newPalletList[index].selected = checked
    setPalletList(newPalletList)

    // 更新全选状态
    const allSelected = newPalletList.every(item => item.selected)
    setSelectAll(allSelected)
  }

  // Split 拆分操作
  const handleSplit = async (poDetailId: number) => {
    const result = await SweetAlertService.confirmMessage('Are you sure you want to split this item to a new pallet?')

    if (result) {
      try {
        await ApiRequest({
          url: `PurchaseOrder/SplitItem?poDetailId=${poDetailId}`,
          method: 'post',
          isShowSpinner: true
        })
        await SweetAlertService.successMessage('Split successful')
        // 重新获取 Item 详情以更新 RevAreaQty 和 PutAwayQty
        await fetchItemDetails()
        onSuccess()
      } catch (error) {
        message.error('Split failed')
      }
    }
  }

  // Merge 合并操作
  const handleMerge = async () => {
    const selectedList = poData?.poDetail?.filter((item: any) => selectedItems.has(item.poDetailId)) || []

    if (selectedList.length < 2) {
      message.error('Please select at least 2 items to merge')
      return
    }

    // 验证是否有 Receiving Qty 数据
    const itemsWithReceivingQty = selectedList.filter((item: any) => {
      const pendingQty = getPendingQty(item)
      return pendingQty > 0
    })

    if (itemsWithReceivingQty.length === 0) {
      message.error('Cannot operate without product')
      return
    }

    // 如果部分物料没有数量，给出警告但继续
    if (itemsWithReceivingQty.length < selectedList.length) {
      const itemsWithoutQty = selectedList.filter((item: any) => getPendingQty(item) === 0)
      const itemCodes = itemsWithoutQty.map((item: any) => item.rawMaterial?.rawMaterialCode).join(', ')
      message.warning(`The following items have no qty in inspection and will be skipped: ${itemCodes}`)
    }

    // 只传递有数量的物料
    if (onOpenMergeToPalletModal) {
      onOpenMergeToPalletModal(itemsWithReceivingQty)
    }
  }

  // === 辅助函数：获取产品信息 ===
  const fetchProductInfo = async (poDetailItem: any) => {
    try {
      // 获取原材料详情
      const rawMaterialResponse = await ApiRequest({
        url: `RawMaterial/GetRawMaterialById?id=${poDetailItem.rawMaterial?.rawMaterialId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (!rawMaterialResponse?.data?.data) {
        throw new Error('Failed to get raw material information')
      }

      const rawMaterialData = rawMaterialResponse.data.data

      if (!rawMaterialData.productId) {
        throw new Error('Raw material has no associated product')
      }

      // 获取产品详情
      const productResponse = await ApiRequest({
        url: `Product/GetProductById?id=${rawMaterialData.productId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (!productResponse?.data?.data) {
        throw new Error('Failed to get product information')
      }

      return {
        rawMaterial: rawMaterialData,
        product: productResponse.data.data
      }
    } catch (error) {
      console.error('Failed to get product info:', error)
      throw error
    }
  }

  // === 辅助函数：获取包装信息 ===
  const getPackagingInfo = (productData: any) => {
    const baseProduct = productData.baseProduct || {}
    const packagingType = baseProduct.packagingType || {}

    return {
      quantity: packagingType.quantity || 0,
      sleevePkt: packagingType.sleevePkt || 0,
      sleeveQty: packagingType.sleeveQty || 0,
      packaging: packagingType.sleevePkt && packagingType.sleeveQty
        ? `${packagingType.sleevePkt}pcs X ${packagingType.sleeveQty}pk`
        : '',
      madeInNz: (productData.logoType !== 3) ? 'Made In New Zealand' : '',
      grossWeigh: packagingType.grossWeigh || 0,
      cartonLength: packagingType.cartonLength || 0,
      cartonWidth: packagingType.cartonWidth || 0,
      cartonHeight: packagingType.cartonHeight || 0
    }
  }

  // === 辅助函数：生成单个sticker数据 ===
  const generateSingleStickerData = (
    box: any,
    productData: any,
    packagingInfo: any,
    logoBase64: string,
    poNumber: string
  ): IStickerPdf => {
    const boxBarcode = box.barCode || box.BarCode

    return {
      barcode: boxBarcode,
      productName: productData.productName || '',
      logoType: productData.logoType || 0,
      logoUrl: productData.logo || '',
      logoBase64: logoBase64,
      productCode: productData.productCode || '',
      custProductCode: productData.custProductCode || '',
      packaging: packagingInfo.packaging,
      sleeveQty: packagingInfo.sleeveQty,
      sleevePkt: packagingInfo.sleevePkt,
      madeInNz: packagingInfo.madeInNz,
      testLogo: '',
      manufactureDate: null,
      quantity: packagingInfo.quantity.toString(),
      batchNumber: poNumber || '',
      grossWeigh: packagingInfo.grossWeigh ? `${packagingInfo.grossWeigh} kg` : '',
      cartonLength: packagingInfo.cartonLength,
      cartonWidth: packagingInfo.cartonWidth,
      cartonHeight: packagingInfo.cartonHeight
    }
  }

  // === 辅助函数：批量生成sticker数据 ===
  const generateStickersData = async (
    boxes: any[],
    productData: any,
    poNumber: string
  ): Promise<IStickerPdf[]> => {
    if (boxes.length === 0) return []

    const packagingInfo = getPackagingInfo(productData)
    const logoBase64 = await fetchLogoBase64()
    const hasValidLogo = logoBase64 && typeof logoBase64 === 'string' && logoBase64.startsWith('data:image')

    return boxes
      .filter((box: any) => {
        const boxBarcode = box.barCode || box.BarCode
        return boxBarcode && boxBarcode.trim() !== ''
      })
      .map((box: any) =>
        generateSingleStickerData(
          box,
          productData,
          packagingInfo,
          hasValidLogo ? logoBase64 : '',
          poNumber
        )
      )
  }

  // 打印单个物料的产品条码
  const handlePrintItemLabel = async (poDetailId: number) => {
    await handlePrintBoxesByStatus(poDetailId, false) // 打印未上架的boxes
  }

  // 打印单个物料已上架的产品条码
  const handlePrintPutawayItemLabel = async (poDetailId: number) => {
    await handlePrintBoxesByStatus(poDetailId, true) // 打印已上架的boxes
  }

  // 通用的box打印函数（重构版）
  const handlePrintBoxesByStatus = async (poDetailId: number, printOnPlate: boolean) => {
    try {
      const poDetailItem = poData?.poDetail?.find((detail: any) => detail.poDetailId === poDetailId)

      if (!poDetailItem) {
        message.error('Cannot find corresponding item information')
        return
      }

      console.log(`=== Printing ${printOnPlate ? 'Putaway' : 'Not Putaway'} boxes ===`)

      // 使用辅助函数获取产品信息
      const { rawMaterial, product } = await fetchProductInfo(poDetailItem)
      const statusText = printOnPlate ? 'Putaway' : 'Not Putaway'

      console.log('Product ID:', rawMaterial.productId)
      console.log('Product code:', rawMaterial.rawMaterialCode)

      // 调用 GetPoBoxesGrouped API 获取box信息
      const boxesResponse = await ApiRequest({
        url: `PurchaseOrder/GetPoBoxesGrouped?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: true
      })

      if (!boxesResponse?.data?.data) {
        message.error('Failed to get box information')
        return
      }

      const boxesGroupedData = boxesResponse.data.data

      // 查找对应产品的boxes
      const productGroup = boxesGroupedData.products?.find((p: any) =>
        p.productId === rawMaterial.productId ||
        p.productCode === rawMaterial.rawMaterialCode
      )

      if (!productGroup || !productGroup.boxes || productGroup.boxes.length === 0) {
        message.warning('This product has no box information, cannot print product barcode')
        return
      }

      console.log('Found product group:', productGroup.productCode)
      console.log('Total box quantity:', productGroup.boxes.length)

      // 根据 printOnPlate 参数过滤 boxes
      const targetBoxes = productGroup.boxes.filter((box: any) => {
        const onPlate = box.onPlate === true || box.OnPlate === true
        return printOnPlate ? onPlate : !onPlate
      })

      console.log(`${statusText} boxes quantity:`, targetBoxes.length)

      if (targetBoxes.length === 0) {
        const warningMsg = printOnPlate
          ? 'This product has no putaway boxes, cannot print'
          : 'All boxes of this product are putaway, no need to print'
        message.warning(warningMsg)
        return
      }

      // 按 Barcode 排序
      targetBoxes.sort((a: any, b: any) => {
        const barcodeA = (a.barCode || a.BarCode || '').toString().toLowerCase()
        const barcodeB = (b.barCode || b.BarCode || '').toString().toLowerCase()
        return barcodeA.localeCompare(barcodeB)
      })

      console.log('✅ Boxes sorted by Barcode')

      // 使用辅助函数批量生成sticker数据
      const stickersArray = await generateStickersData(targetBoxes, product, poData?.poNumber || '')

      if (stickersArray.length === 0) {
        message.error('No valid box data to print')
        return
      }

      console.log(`✅ Generated ${stickersArray.length} stickers`)

      // 使用统一的生成器打印
      await productStickersPdfGenerateAll(stickersArray, 'print')

      // 根据产品类型显示不同的成功消息
      const productTypeDesc = product.logoType === 3 ? 'External Product' : 'Product'
      message.success(`Successfully printed ${stickersArray.length} ${productTypeDesc} barcodes (${statusText}box)`)

    } catch (error) {
      console.error('Product barcode print failed:', error)
      message.error('Product barcode print failed')
    }
  }

  // 获取 logo base64
  const fetchLogoBase64 = async (): Promise<string> => {
    try {
      const response = await fetch('/logo.png')
      if (!response.ok) {
        return ''
      }
      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = () => resolve('')
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      return ''
    }
  }

  // 批量打印托盘标签
  const handlePrintPalletLabels = async () => {
    const selectedPallets = palletList.filter(item => item.selected)

    if (selectedPallets.length === 0) {
      message.error('Please select at least one pallet to print')
      return
    }

    try {
      // 提取唯一的托盘号
      const uniquePalletNumbers = Array.from(new Set(selectedPallets.map(item => item.palletNumber)))

      // 使用 palletSticker 函数打印托盘条码（与其他地方的托盘打印一致）
      if (uniquePalletNumbers.length > 0) {
        palletSticker(uniquePalletNumbers, 'print')
        message.success(`Successfully printed ${uniquePalletNumbers.length} pallet labels`)
      }
    } catch (error) {
      message.error('Pallet label print failed')
    }
  }

  // 打印选中托盘的所有产品箱子标签（重构版）
  const handlePrintPalletBoxesLabels = async () => {
    const selectedPallets = palletList.filter(item => item.selected)

    if (selectedPallets.length === 0) {
      message.error('Please select at least one pallet to print')
      return
    }

    try {
      console.log('=== Starting printing pallet product labels ===')
      console.log('Selected pallets:', selectedPallets)

      // 调用 API 获取该PO的所有 boxes 信息
      const boxesResponse = await ApiRequest({
        url: `PurchaseOrder/GetPoBoxesGrouped?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: true
      })

      if (!boxesResponse?.data?.data) {
        message.error('Failed to get box information')
        return
      }

      const boxesGroupedData = boxesResponse.data.data
      console.log('API returned boxes data:', boxesGroupedData)

      if (!boxesGroupedData.products || boxesGroupedData.products.length === 0) {
        message.warning('No product boxes data found')
        return
      }

      const allStickers: IStickerPdf[] = []

      // 为每个选中托盘的 items 生成产品标签
      for (const item of selectedPallets) {
        console.log(`\nProcessing pallet item: ${item.palletNumber} - ${item.itemCode}`)

        try {
          // 找到对应的 poDetail
          const poDetailItem = poData?.poDetail?.find((detail: any) =>
            detail.rawMaterial?.rawMaterialCode === item.itemCode
          )

          if (!poDetailItem) {
            console.warn(`Cannot find corresponding poDetail: ${item.itemCode}`)
            continue
          }

          // 使用辅助函数获取产品信息
          const { rawMaterial, product } = await fetchProductInfo(poDetailItem)

          console.log(`Product data:`, product.productCode, product.productName)

          // 查找该产品的 boxes
          const productGroup = boxesGroupedData.products?.find((p: any) =>
            p.productId === rawMaterial.productId ||
            p.productCode === rawMaterial.rawMaterialCode ||
            p.productCode === product.productCode
          )

          if (!productGroup) {
            console.warn(`Product not found in boxes data: ${rawMaterial.rawMaterialCode}`)
            continue
          }

          console.log(`Found product group:`, productGroup.productCode, `boxes quantity:`, productGroup.boxes?.length || 0)

          if (!productGroup.boxes || productGroup.boxes.length === 0) {
            console.warn(`This product has no boxes: ${productGroup.productCode}`)
            continue
          }

          // 关键修改：只获取选中托盘上的 boxes
          const selectedPalletBoxes = productGroup.boxes.filter((box: any) => {
            // 首先检查是否在托盘上
            const onPlate = box.onPlate === true || box.OnPlate === true

            if (!onPlate) {
              return false
            }

            // 然后检查托盘号是否匹配
            const boxPalletNumber = box.palletNumber || box.PalletNumber ||
                                   box.plateCode || box.PlateCode ||
                                   box.palletCode || box.PalletCode

            const isMatch = boxPalletNumber === item.palletNumber

            if (boxPalletNumber && !isMatch) {
              console.log(`  Box ${box.barCode || box.BarCode}: Pallet number ${boxPalletNumber} != selected pallet ${item.palletNumber} ✗`)
            } else if (isMatch) {
              console.log(`  Box ${box.barCode || box.BarCode}: Pallet number ${boxPalletNumber} ✓ match`)
            } else if (!boxPalletNumber) {
              console.log(`  Box ${box.barCode || box.BarCode}: No pallet number information`)
            }

            return isMatch
          })

          console.log(`Number of boxes on pallet ${item.palletNumber}: ${selectedPalletBoxes.length}`)

          if (selectedPalletBoxes.length === 0) {
            console.warn(`No boxes of this product found on pallet ${item.palletNumber}`)
            continue
          }

          // 按 Barcode 排序
          selectedPalletBoxes.sort((a: any, b: any) => {
            const barcodeA = (a.barCode || a.BarCode || '').toString().toLowerCase()
            const barcodeB = (b.barCode || b.BarCode || '').toString().toLowerCase()
            return barcodeA.localeCompare(barcodeB)
          })

          console.log(`✅ Boxes on pallet ${item.palletNumber} sorted by Barcode`)

          // 使用辅助函数批量生成sticker数据
          const stickersArray = await generateStickersData(selectedPalletBoxes, product, poData?.poNumber || '')

          console.log(`Pallet ${item.palletNumber} generated ${stickersArray.length} stickers`)

          allStickers.push(...stickersArray)

        } catch (error) {
          console.error(`Error processing item ${item.itemCode}:`, error)
        }
      }

      console.log(`\nTotal generated ${allStickers.length} stickers`)

      if (allStickers.length === 0) {
        message.warning('Selected pallets have no printable product labels (no putaway boxes)')
        return
      }

      // 批量打印所有 stickers
      await productStickersPdfGenerateAll(allStickers, 'print')
      message.success(`Successfully printed ${allStickers.length} product labels`)

    } catch (error) {
      console.error('Product label print failed:', error)
      message.error('Product label print failed')
    }
  }

  // 取消操作
  const handleCancel = () => {
    setPalletList([])
    setSelectAll(false)
    onClose()
  }

  // 更新上架
  const handleUpdate = async () => {
    setLoading(true)

    try {
      await ApiRequest({
        url: `PurchaseOrder/UpdatePutaway?poId=${poData.poId}`,
        method: 'post',
        data: {
          palletList: palletList
        },
        isShowSpinner: true
      })

      await SweetAlertService.successMessage('Putaway update successful')
      // 重新获取 Item 详情以更新 RevAreaQty 和 PutAwayQty
      await fetchItemDetails()
      handleCancel()
      onSuccess()
    } catch (error) {
      message.error('Putaway update failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      width={1600}
      footer={null}
      closable={true}
      style={{ top: 20 }}
      bodyStyle={{ padding: '20px', height: 'calc(100vh - 140px)', overflowY: 'auto' }}
    >
      <div style={modalContainerStyle}>
        {/* 页头标题 */}
        <div style={titleStyle}>
          PO Putaway Board
        </div>

        {/* 基础信息区 */}
        <div style={gradientHeaderStyle}>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={headerItemStyle}>
              <span style={headerLabelStyle}>PO Number:</span>
              <span style={headerValueStyle}>
                {poData?.poNo || 'N/A'}
              </span>
            </div>
            <div style={headerItemStyle}>
              <span style={headerLabelStyle}>Supplier:</span>
              <span style={headerValueStyle}>
                {poData?.suplier?.suplierName || 'N/A'}
              </span>
            </div>
            <div style={headerItemStyle}>
              <span style={headerLabelStyle}>Creator:</span>
              <span style={headerValueStyle}>
                {poData?.createdEmployee?.firstName} {poData?.createdEmployee?.lastName || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* 收货明细与上架操作列表 */}
        <div style={{ marginBottom: '20px' }}>
          <table style={{ width: '100%', ...tableBorder, borderRadius: modalStyles.borderRadiusSmall, overflow: 'hidden', boxShadow: modalStyles.shadowSmall }}>
            <thead>
              <tr style={tableHeaderStyle}>
                <th style={{ ...tableCellStyle, textAlign: 'center', width: '50px' }}>
                  <Checkbox
                    checked={selectedItems.size === poData?.poDetail?.length && poData?.poDetail?.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th style={{ ...tableCellStyle, textAlign: 'left' }}>Item Code</th>
                <th style={{ ...tableCellStyle, textAlign: 'left' }}>Description</th>
                <th style={{ ...tableCellStyle, textAlign: 'center' }}>Qty/unit</th>
                <th style={{ ...tableCellStyle, textAlign: 'center' }}>qty in inspection</th>
                <th style={{ ...tableCellStyle, textAlign: 'center' }}>Putaway Qty</th>
                <th style={{ ...tableCellStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {poData?.poDetail?.map((item: any, index: number) => (
                <tr key={item.poDetailId} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc', transition: 'background-color 0.2s' }}>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', textAlign: 'center' }}>
                    <Checkbox
                      checked={selectedItems.has(item.poDetailId)}
                      onChange={(e) => handleItemSelect(item.poDetailId, e.target.checked)}
                    />
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', color: modalStyles.textPrimary }}>
                    {item.rawMaterial?.rawMaterialCode || 'N/A'}
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', maxWidth: '300px', color: modalStyles.textPrimary }}>
                    <div style={{ wordBreak: 'break-word' }}>
                      {item.rawMaterial?.rawMaterialName || 'N/A'}
                    </div>
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', textAlign: 'center', color: modalStyles.textPrimary, fontWeight: 500 }}>
                    {getPackagingQty(item)}
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: modalStyles.info }}>{getPendingQty(item)}</span>
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => handlePrintItemLabel(item.poDetailId)}
                        style={{ lineHeight: '1.2', padding: '4px 12px', fontSize: '13px', fontWeight: 500, backgroundColor: modalStyles.info, borderColor: modalStyles.info }}
                      >
                        Print
                      </Button>
                    </div>
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: modalStyles.warning }}>{getPutawayQty(item)}</span>
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => handlePrintPutawayItemLabel(item.poDetailId)}
                        style={{ lineHeight: '1.2', padding: '4px 12px', fontSize: '13px', fontWeight: 500, backgroundColor: modalStyles.warning, borderColor: modalStyles.warning }}
                      >
                        Print
                      </Button>
                    </div>
                  </td>
                  <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', textAlign: 'center' }}>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => onOpenAutoSplitModal && onOpenAutoSplitModal(poData, item)}
                      style={{
                        lineHeight: '1.2',
                        padding: '4px 12px',
                        position: 'relative',
                        backgroundColor: modalStyles.success,
                        borderColor: modalStyles.success,
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                      }}
                    >
                      Split
                      <ThunderboltOutlined style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        fontSize: '8px',
                        color: '#93c5fd'
                      }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Merge 按钮 - 跨行合并 */}
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              onClick={handleMerge}
              style={{
                lineHeight: '1.2',
                padding: '8px 20px',
                position: 'relative',
                backgroundColor: modalStyles.warning,
                borderColor: modalStyles.warning,
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                borderRadius: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Merge
              <ThunderboltOutlined style={{
                position: 'absolute',
                top: '3px',
                right: '3px',
                fontSize: '10px',
                color: '#3b82f6'
              }} />
            </Button>
          </div>
        </div>

        {/* 托盘/上架日志记录区 */}
        <div style={{ marginBottom: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', backgroundImage: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)' }}>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>Pallet Number</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>Item code</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Qty</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Created at</th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>
                  <Checkbox
                    checked={selectAll}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                  >
                    Select all
                  </Checkbox>
                </th>
                <th style={{ border: '1px solid #e5e7eb', padding: '12px 8px', fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {palletStructure.map((pallet, palletIndex) => (
                pallet.items.map((item: any, itemIndex: number) => {
                  const globalIndex = palletStructure.slice(0, palletIndex).reduce((acc, p) => acc + p.items.length, 0) + itemIndex
                  return (
                    <tr key={`${pallet.palletNumber}-${itemIndex}`} style={{ backgroundColor: globalIndex % 2 === 0 ? '#ffffff' : '#f8fafc', transition: 'background-color 0.2s' }}>
                      {/* Pallet Number - 跨行合并 */}
                      {itemIndex === 0 && (
                        <td
                          rowSpan={pallet.rowspan}
                          style={{
                            border: '1px solid #e5e7eb',
                            padding: '10px 8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            color: '#8b5cf6',
                            backgroundColor: globalIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
                          }}
                        >
                          {pallet.palletNumber}
                        </td>
                      )}
                      <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', color: '#334155' }}>
                        {item.itemCode || 'N/A'}
                      </td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', maxWidth: '300px', color: '#334155' }}>
                        <div style={{ wordBreak: 'break-word' }}>
                          {item.description || 'N/A'}
                        </div>
                      </td>
                      <td style={{ border: '1px solid #e5e7eb', padding: '10px 8px', fontSize: '14px', textAlign: 'center', color: '#334155', fontWeight: 500 }}>
                        {item.quantity}
                      </td>
                      {/* Created at - 跨行合并 */}
                      {itemIndex === 0 && (
                        <td
                          rowSpan={pallet.rowspan}
                          style={{
                            border: '1px solid #e5e7eb',
                            padding: '10px 8px',
                            fontSize: '14px',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            color: '#64748b',
                            backgroundColor: globalIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
                          }}
                        >
                          {moment.utc(pallet.createdAt).local().format('HH:mm DD/MM/YYYY')}
                        </td>
                      )}
                      {/* Select all checkbox - 跨行合并 */}
                      {itemIndex === 0 && (
                        <td
                          rowSpan={pallet.rowspan}
                          style={{
                            border: '1px solid #e5e7eb',
                            padding: '10px 8px',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            backgroundColor: globalIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
                          }}
                        >
                          <Checkbox
                            checked={pallet.selected}
                            onChange={(e) => {
                              const newPalletList = [...palletList]
                              newPalletList.forEach(palletItem => {
                                if (palletItem.palletNumber === pallet.palletNumber) {
                                  palletItem.selected = e.target.checked
                                }
                              })
                              setPalletList(newPalletList)

                              // 更新全选状态
                              const allSelected = newPalletList.every(palletItem => palletItem.selected)
                              setSelectAll(allSelected)
                            }}
                          />
                        </td>
                      )}
                      {/* Actions - 通栏合并 */}
                      {itemIndex === 0 && palletIndex === 0 && (
                        <td
                          rowSpan={palletList.length}
                          style={{
                            border: '1px solid #e5e7eb',
                            padding: '10px 8px',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            backgroundColor: '#f8fafc'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                            <Button
                              type="primary"
                              onClick={handlePrintPalletLabels}
                              style={{
                                lineHeight: '1.2',
                                padding: '6px 16px',
                                fontSize: '14px',
                                fontWeight: 500,
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                                backgroundColor: '#3b82f6',
                                borderColor: '#3b82f6'
                              }}
                            >
                              Print Pallet
                            </Button>
                            <Button
                              type="primary"
                              onClick={handlePrintPalletBoxesLabels}
                              style={{
                                lineHeight: '1.2',
                                padding: '6px 16px',
                                fontSize: '14px',
                                fontWeight: 500,
                                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                                backgroundColor: '#10b981',
                                borderColor: '#10b981'
                              }}
                            >
                              Print Boxes
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })
              ))}
            </tbody>
          </table>
        </div>

        {/* 底部全局操作区 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
          <Button
            onClick={handleCancel}
            style={{
              backgroundColor: '#fff',
              borderColor: '#3b82f6',
              color: '#3b82f6',
              minWidth: '120px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#eff6ff';
              e.currentTarget.style.borderColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.borderColor = '#3b82f6';
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default PurchaseOrderPutawayModal
